import React, { FC, useEffect, useMemo } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { Stack, Box, useMediaQuery, useTheme } from '@mui/material';
import { useQuery } from '@apollo/client';

import { LoadingIndicator } from '@pv/common/components';
import { ProfileGoogleMap } from '../../components/ProfileGoogleMap';
import { buildAvailableSpacesRequest } from '../../helpers/api/spaces';
import { buildOrganizationCenterCoord } from '../../helpers/api/organizations';
import { FilterBar, FilterBarProvider } from '../../components/FilterBar';
import { SpacesShelf } from '../../components/SpacesShelf';
import {
  fetchOrganization,
  useAvailableSpaces,
} from '../../helpers/api/graphql';
import { buildMappableAddressCoord } from '../../helpers/api/addresses';
import { FooterBar } from '../../components/FooterBar';
import {
  parseExpressBookSettingsFromVenues,
  parseHoursOfOperationFromVenues,
} from '../../helpers/api/organizations';
import styled from 'styled-components';
import { useAvailability } from '../../providers/AvailabilityProvider';
import {
  MarketplaceOrganizationQuery,
  MarketplaceOrganizationQueryVariables,
} from '@pv/common/graphql/operations';

interface OrganizationSpacesPageProps {}

const LogoImg = styled.img`
  object-fit: contain;
  height: 100%;
`;

export const OrganizationSpacesPage: FC<OrganizationSpacesPageProps> = (
  props: OrganizationSpacesPageProps,
) => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const xlMedia = useMediaQuery(theme.breakpoints.only('xl'));
  const lgMedia = useMediaQuery(theme.breakpoints.only('lg'));
  const smMedia = useMediaQuery(theme.breakpoints.down('md'));
  const {
    dateTimeFilter,
    setDateTimeFilter,
    capacityFilterValues,
    setCapacityFilterValues,
    startDate,
    startOffset,
    endOffset,
  } = useAvailability();

  const {
    data: organizationData,
    loading: organizationLoading,
    error,
  } = useQuery<
    MarketplaceOrganizationQuery,
    MarketplaceOrganizationQueryVariables
  >(fetchOrganization, {
    variables: {
      slug: slug || '',
    },
    skip: !slug,
  });

  const {
    data: availableSpacesData,
    loading: spacesLoading,
    refetch,
  } = useAvailableSpaces({ organizationSlug: slug }, !slug);

  const buildMapWidth = () => {
    if (xlMedia) {
      return '25vw';
    }

    if (lgMedia) {
      return '33vw';
    }

    return '50vw';
  };

  const mapStyle = {
    width: buildMapWidth(),
    height: '78vh',
  };

  useEffect(() => {
    if (!slug) {
      return;
    }

    refetch(
      buildAvailableSpacesRequest({
        organizationSlug: slug,
        startDate,
        startOffset,
        endOffset,
        capacityFilterValues,
      }),
    );
  }, [refetch, slug, startDate, startOffset, endOffset, capacityFilterValues]);

  const organizationProfile = organizationData?.marketplaceOrganization;
  const organizationVenues = useMemo(
    () => organizationProfile?.venues || [],
    [organizationProfile],
  );

  const availableSpaces = useMemo(
    () => availableSpacesData?.availableSpaces || [],
    [availableSpacesData],
  );

  const mappableVenues = React.useMemo(() => {
    return organizationVenues.map((venue) => ({
      key: venue.slug,
      name: venue.name,
      position: buildMappableAddressCoord(venue.address || undefined),
      spaces: availableSpaces?.filter(
        (space) => space.venue.slug === venue.slug,
      ),
      address: venue.address,
      handleClick: () => navigate(`/venues/${venue.slug}`),
    }));
  }, [availableSpaces, organizationVenues, navigate]);

  const renderProfileLogo = () => {
    const imgPath = organizationProfile?.logoUrl
      ? organizationProfile?.logoUrl
      : '/default_logo.png';

    return (
      <LogoImg
        alt={`${organizationProfile?.name} Logo`}
        src={imgPath}
        style={{ width: '300px', height: '150px' }}
      />
    );
  };

  const expressBookSettings = React.useMemo(() => {
    if (!organizationProfile) {
      return {
        minLeadTimeDays: 0,
        maxLeadTimeDays: 360,
        minEventDurationHours: 0,
        maxEventDurationHours: 24,
      };
    }
    return parseExpressBookSettingsFromVenues(organizationVenues);
  }, [organizationVenues, organizationProfile]);

  // Filter bar state for provider
  const filterBarState = {
    mode: 'profile' as const,
    dateTimeFilter,
    setDateTimeFilter,
    capacityFilterValues,
    setCapacityFilterValues,
    expressBookSettings,
    hoursOfOperation: parseHoursOfOperationFromVenues(
      organizationVenues,
    ),
  };

  if (organizationLoading) {
    return <LoadingIndicator />;
  }
  if (error && error.graphQLErrors[0]?.message === 'No organization found') {
    return <Navigate to="/not-found" replace={false} />;
  }

  return (
    <>
      <Stack
        direction="column"
        alignItems="center"
        gap="12px"
        sx={{
          boxShadow: '0px -1px 0px 0px rgba(0, 0, 0, 0.1) inset',
        }}
      >
        <Stack
          direction="column"
          alignItems="center"
          sx={{
            width: '100%',
          }}
 
        >
          {renderProfileLogo()}
          <Box sx={{ display: 'flex', flexGrow: 1, width: '100%', padding: '24px', justifyContent: 'center' }}>

          <FilterBarProvider state={filterBarState}>
            <FilterBar />
          </FilterBarProvider>
          </Box>
        </Stack>
      </Stack>
      <Stack direction="row">
        <Stack
          justifyContent="space-between"
          sx={{
            padding: '24px',
            maxHeight: '78vh',
            overflowY: 'scroll',
            width: {
              sm: '100vw',
              md: '50vw',
              lg: '67vw',
              xl: '75vw',
            },
          }}
        >
          {spacesLoading ? (
            <LoadingIndicator />
          ) : (
            <SpacesShelf
              spaces={availableSpaces.map((availableSpace) => ({
                slug: availableSpace.slug,
                name: availableSpace.name,
                description: availableSpace.description,
                images: availableSpace.images.filter(
                  (image): image is { url: string } => !!image.url,
                ),
                pathname: availableSpace.pathname,
                standing: availableSpace.standing || undefined,
                seated: availableSpace.seated || undefined,
                venue: {
                  liveAvailability: availableSpace.venue.liveAvailability,
                  images: availableSpace.venue.images.filter(
                    (image): image is { url: string } => !!image.url,
                  ),
                  profileBanner:
                    availableSpace.venue.profileBanner || undefined,
                  name: availableSpace.venue.name,
                  slug: availableSpace.venue.slug,
                  priceCategory: availableSpace.venue.priceCategory,
                  address: availableSpace.venue.address
                    ? {
                        city: availableSpace.venue.address.city || '',
                        state: availableSpace.venue.address.state || '',
                      }
                    : undefined,
                },
              }))}
              gridTemplateColumns={{
                xs: '1fr',
                sm: '1fr',
                md: '1fr',
                lg: 'repeat(2, 1fr)',
                xl: 'repeat(3, 1fr)',
              }}
            />
          )}
          <FooterBar />
        </Stack>
        {organizationProfile &&
          !smMedia &&
          mappableVenues &&
          organizationProfile.latitude &&
          organizationProfile.longitude && (
            <ProfileGoogleMap
              mapStyle={mapStyle}
              center={buildOrganizationCenterCoord({
                latitude: organizationProfile.latitude,
                longitude: organizationProfile.longitude,
              })}
              mappableItems={mappableVenues.map((venue) => ({
                ...venue,
                address: venue.address || undefined,
                spaces: venue.spaces?.map((space) => ({
                  ...space,
                  standing: space.standing || undefined,
                  seated: space.seated || undefined,
                  images: space.images.filter(
                    (image): image is { url: string } => !!image.url,
                  ),
                  address: space.venue.address || undefined,
                })),
              }))}
            />
          )}
      </Stack>
    </>
  );
};
